// Deterministic en/ru/uz date formatting.
//
// `toLocaleDateString('uz-Latn-UZ', …)` is not safe to depend on: a runtime
// without Uzbek CLDR data silently falls back to the *root* locale, which
// renders months as "M07"/"M08" rather than month names. That shipped a billing
// period as "M07 30 – M08 30" and a renewal date as "2026 M08 30" — technically
// a date, unreadable in practice. Spelling out the month names for the three
// languages we ship removes the dependency on the host's locale data, so these
// render identically in every browser, in the PDF renderer, and in tests.
//
// Day/month order follows each language's own convention (CLDR patterns
// `MMMM d, y` / `d MMMM y` / `d-MMMM, y`).

import i18n from 'i18n';

export type DateLang = 'en' | 'ru' | 'uz';

const MONTHS_LONG: Record<DateLang, readonly string[]> = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  // Genitive — the form Russian uses when the day precedes the month.
  ru: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  uz: ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr'],
};

const MONTHS_SHORT: Record<DateLang, readonly string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  ru: ['янв.', 'февр.', 'мар.', 'апр.', 'мая', 'июн.', 'июл.', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'],
  // CLDR abbreviates June/July to "iyn"/"iyl", which reads as noise next to a
  // number; the full four-letter forms are barely longer and unambiguous.
  uz: ['yan', 'fev', 'mar', 'apr', 'may', 'iyun', 'iyul', 'avg', 'sen', 'okt', 'noy', 'dek'],
};

/** Assemble day + month (+ year) in each language's own order. */
const ORDER: Record<DateLang, (day: number, month: string, year?: number) => string> = {
  en: (day, month, year) => (year ? `${month} ${day}, ${year}` : `${month} ${day}`),
  ru: (day, month, year) => (year ? `${day} ${month} ${year}` : `${day} ${month}`),
  uz: (day, month, year) => (year ? `${day}-${month}, ${year}` : `${day}-${month}`),
};

/** Active app language, narrowed to a language we have month names for. */
export function activeDateLang(): DateLang {
  const base = (i18n.language ?? 'en').split('-')[0];
  return base in MONTHS_LONG ? (base as DateLang) : 'en';
}

function toDate(value?: string | Date | number | null): Date | null {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function format(
  value: string | Date | number | null | undefined,
  months: Record<DateLang, readonly string[]>,
  withYear: boolean,
  fallback: string,
): string {
  const date = toDate(value);
  if (!date) return fallback;
  const lang = activeDateLang();
  return ORDER[lang](date.getDate(), months[lang][date.getMonth()], withYear ? date.getFullYear() : undefined);
}

/** Full month + year: "August 30, 2026" · "30 августа 2026" · "30-avgust, 2026". */
export function formatDateLong(value?: string | Date | number | null, fallback = '—'): string {
  return format(value, MONTHS_LONG, true, fallback);
}

/** Abbreviated month + year: "Aug 30, 2026" · "30 авг. 2026" · "30-avg, 2026". */
export function formatDateShort(value?: string | Date | number | null, fallback = '—'): string {
  return format(value, MONTHS_SHORT, true, fallback);
}

/** Abbreviated month, no year: "Aug 30" · "30 авг." · "30-avg". */
export function formatDayMonth(value?: string | Date | number | null, fallback = '—'): string {
  return format(value, MONTHS_SHORT, false, fallback);
}

/** Full month and 24-hour time, no year, for deadlines: "October 11, 23:59" · "11 октября, 23:59" · "11-oktabr, 23:59". */
export function formatDeadline(value?: string | Date | number | null, fallback = '—'): string {
  const date = toDate(value);
  if (!date) return fallback;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${format(date, MONTHS_LONG, false, fallback)}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Day, month and 24-hour time for list rows: "Sep 28, 14:18" · "28 сент., 14:18" ·
 * "28-sen, 14:18". The year is added once the date falls outside the current year.
 */
export function formatDayTime(value?: string | Date | number | null, fallback = '—'): string {
  const date = toDate(value);
  if (!date) return fallback;
  const day = date.getFullYear() === new Date().getFullYear() ? formatDayMonth(date) : formatDateShort(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${day}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * 24-hour time and date format: "12:34 03-12-2026" (HH:mm DD-MM-YYYY).
 */
export function formatNotificationDateTime(value?: string | Date | number | null, fallback = ''): string {
  const date = toDate(value);
  if (!date) return fallback;

  const pad = (n: number) => String(n).padStart(2, '0');
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();

  return `${hours}:${minutes} ${day}-${month}-${year}`;
}

/**
 * 24-hour time and date format: "22:13 10.12.2026" (HH:mm DD.MM.YYYY).
 */
export function formatHistoryDateTime(value?: string | Date | number | null, fallback = '—'): string {
  const date = toDate(value);
  if (!date) return fallback;

  const pad = (n: number) => String(n).padStart(2, '0');
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();

  return `${hours}:${minutes} ${day}.${month}.${year}`;
}

// Non-breaking space, so a grouped amount never wraps mid-number.
const GROUP_SEPARATOR = ' ';

/**
 * Group thousands with a space ("129 000"), the convention all three languages
 * use for so'm amounts. Hand-rolled for the same reason as the month names:
 * `Intl.NumberFormat('uz-UZ')` degrades to root grouping ("129,000") wherever
 * Uzbek data is missing. Non-integers are rounded — every amount we display
 * (so'm, coins, tokens) is a whole unit.
 */
export function formatGroupedNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(Math.abs(value));
  const grouped = String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
  return value < 0 ? `−${grouped}` : grouped;
}
